/**
 * admin_analytics_controller.js — entity activity analytics panel (Stimulus).
 *
 * Backs the reusable analytics card embedded in the admin business- and
 * user-detail pages (templates/admin/_analytics_panel.html). The card asks one
 * JSON endpoint "how active has this business/user been" and renders:
 *   - a row of lifetime total tiles (all-time row counts), and
 *   - a grid of current-period activity tiles, each with its delta vs the
 *     immediately preceding period of the same length.
 *
 * The data source is the tenant DATABASE (AdminAnalyticsService ->
 * EntityStatsService), not PostHog: the DB is authoritative for "how many quotes
 * did this business accept". Behavioural product analytics layer on separately.
 *
 * Lifecycle: connect() fetches once for the default window and renders; the
 * period buttons re-fetch via setDays(). There is no interval and no document
 * listener, so there is nothing to tear down — connect() re-runs per element on
 * each Turbo navigation against fresh DOM, which is all this needs.
 *
 * Config (data-* on the host element):
 *   data-admin-analytics-url-value   JSON endpoint returning {analytics: {...}}
 *   data-admin-analytics-days-value  initial window size in days (default 30)
 *
 * Targets: status (loading/error line), lifetime (totals row), activity (grid),
 *          period (the day-window button group, for active-state styling).
 */

import { Controller } from "@hotwired/stimulus";

// Fallback tiles for the original business/user activity panel, used only when
// the payload does not declare its own tiles. The newer platform tabs
// (acquisition, revenue, PLG) ship a `tiles: {headline, activity}` block so the
// service that owns the metrics also owns their labels, order and format.
const LIFETIME_TILES = [
    { key: "clients", label: "Clients" },
    { key: "quotes", label: "Quotes" },
    { key: "jobs", label: "Jobs" },
    { key: "invoices", label: "Invoices" },
    { key: "scheduled_events", label: "Scheduled" },
];

const ACTIVITY_TILES = [
    { key: "clients_created", label: "Clients created" },
    { key: "quotes_created", label: "Quotes created" },
    { key: "quotes_accepted", label: "Quotes accepted" },
    { key: "jobs_created", label: "Jobs created" },
    { key: "jobs_completed", label: "Jobs completed" },
    { key: "invoices_sent", label: "Invoices sent" },
    { key: "invoices_paid", label: "Invoices paid" },
    { key: "scheduled_events", label: "Scheduled events" },
    { key: "leads_received", label: "Leads received" },
    { key: "recurring_jobs_created", label: "Recurring jobs" },
];

function esc(value) {
    return String(value == null ? "" : value)
        .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

// Format a tile value by its declared format. Defaults to a plain number so
// older payloads with no per-tile format keep rendering as counts.
function fmt(value, format) {
    const n = Number(value) || 0;
    if (format === "currency") {
        return "$" + n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    if (format === "percent") return `${n.toLocaleString()}%`;
    return n.toLocaleString();
}

export default class extends Controller {
    static values = {
        url: String,
        days: { type: Number, default: 30 },
    };

    static targets = ["status", "lifetime", "activity", "period", "leaderboard", "badge", "gaps"];

    connect() {
        this._load();
    }

    // Period button handler: re-fetch for the chosen window.
    setDays(event) {
        const days = parseInt(event.params.days || event.currentTarget.dataset.days, 10);
        if (!days || days === this.daysValue) return;
        this.daysValue = days;
        this._syncPeriodButtons();
        this._load();
    }

    async _load() {
        this._setStatus("Loading activity…");
        const sep = this.urlValue.includes("?") ? "&" : "?";
        try {
            const res = await fetch(`${this.urlValue}${sep}days=${this.daysValue}`, {
                headers: { Accept: "application/json" },
            });
            const body = await res.json();
            if (!res.ok || !body.success) {
                throw new Error(body.message || `HTTP ${res.status}`);
            }
            this._render(body.analytics);
        } catch (err) {
            this._setStatus(`Could not load analytics: ${esc(err.message)}`, true);
        }
    }

    _render(analytics) {
        const period = analytics.period || {};
        const lifetime = analytics.lifetime || {};
        const current = analytics.current || {};
        const delta = analytics.delta || {};

        // Business/user payloads carry the demo seed totals separately so they
        // are shown beside, never summed into, the real activity above. Absent
        // on the platform tabs and on businesses with no seed data.
        const demo = analytics.demo || {};
        const demoLifetime = demo.lifetime || {};
        const demoCurrent = demo.current || {};

        // Tiles are payload-driven when present (platform tabs), else the
        // built-in business/user defaults.
        const tiles = analytics.tiles || {};
        const headlineTiles = tiles.headline || LIFETIME_TILES;
        const activityTiles = tiles.activity || ACTIVITY_TILES;

        // Demo/test flags live on the resolved entity: business payloads carry
        // them under .business, user payloads at the top level. The platform
        // tabs have no single entity, so no flags and no badge.
        const flags = analytics.business || analytics;
        this._renderBadge(!!flags.is_demo, !!flags.is_test);

        if (this.hasLifetimeTarget) {
            this.lifetimeTarget.innerHTML = headlineTiles
                .map((t) => this._lifetimeTile(t, lifetime[t.key] || 0, demoLifetime[t.key] || 0))
                .join("");
        }
        if (this.hasActivityTarget) {
            this.activityTarget.innerHTML = activityTiles
                .map((t) => this._activityTile(t, current[t.key] || 0, delta[t.key] || 0, demoCurrent[t.key] || 0))
                .join("");
        }
        if (this.hasLeaderboardTarget) {
            // PLG ships a pre-ranked `leaderboard`; business activity ships
            // `businesses` with its own column set.
            if (analytics.leaderboard) {
                this._renderPlgLeaderboard(analytics.leaderboard);
            } else {
                this._renderLeaderboard(analytics.businesses || []);
            }
        }
        this._renderGaps(analytics.gaps || []);

        const cur = period.current || {};
        let note = "";
        if (flags.is_test) {
            note = " Test business — excluded from platform analytics.";
        } else if (flags.is_demo) {
            note = " Figures are this business's real activity; demo seed data is shown separately and excluded from platform totals.";
        }
        this._setStatus(`Data from ${esc(cur.from)} to ${esc(cur.to)} (last ${this.daysValue} days), compared with the prior ${this.daysValue} days.${note}`);
        this._syncPeriodButtons();
    }

    // Honest "not measured yet" note for the PLG tab: the parts of the intended
    // funnel that have no data producer. Rendered only where a gaps target
    // exists so other panels are unaffected.
    _renderGaps(gaps) {
        if (!this.hasGapsTarget) return;
        if (!gaps.length) {
            this.gapsTarget.innerHTML = "";
            return;
        }
        const items = gaps.map((g) => `<li>${esc(g)}</li>`).join("");
        this.gapsTarget.innerHTML = `
            <div class="alert alert-light border small mb-0">
                <strong>Not tracked yet:</strong>
                <ul class="mb-0 mt-1">${items}</ul>
            </div>`;
    }

    // Demo/test pill in the card header. Demo takes precedence in the label, but
    // a row can be both; we surface whichever applies, demo first.
    _renderBadge(isDemo, isTest) {
        if (!this.hasBadgeTarget) return;
        if (isDemo) {
            this.badgeTarget.innerHTML = `<span class="badge bg-warning text-dark ms-2">Demo data</span>`;
        } else if (isTest) {
            this.badgeTarget.innerHTML = `<span class="badge bg-secondary ms-2">Test data</span>`;
        } else {
            this.badgeTarget.innerHTML = "";
        }
    }

    // Muted "demo seed" footnote, rendered only when the entity carries seeded
    // sample rows. Keeps the seed count visible but plainly separate from the
    // real figure above it.
    _demoNote(demoValue, format) {
        const n = Number(demoValue) || 0;
        if (n <= 0) return "";
        return `<div class="aa-demo">+${esc(fmt(n, format))} demo seed</div>`;
    }

    _lifetimeTile(tile, value, demoValue) {
        return `
            <div class="admin-analytics-lifetime-tile">
                <div class="aa-lt-value">${esc(fmt(value, tile.format))}</div>
                <div class="aa-lt-label">${esc(tile.label)}</div>
                ${this._demoNote(demoValue, tile.format)}
            </div>`;
    }

    _activityTile(tile, value, delta, demoValue) {
        const dir = delta > 0 ? "up" : delta < 0 ? "down" : "flat";
        const arrow = delta > 0 ? "▲" : delta < 0 ? "▼" : "—";
        const sign = delta > 0 ? "+" : "";
        const deltaText = delta === 0 ? "no change" : `${sign}${delta.toLocaleString()}`;
        return `
            <div class="admin-analytics-tile">
                <div class="aa-value">${esc(fmt(value, tile.format))}</div>
                <div class="aa-label">${esc(tile.label)}</div>
                <div class="aa-delta aa-delta-${dir}">${arrow} ${esc(deltaText)}</div>
                ${this._demoNote(demoValue, tile.format)}
            </div>`;
    }

    // PLG per-business ranking: who is driving online quote acceptance. Pre-sorted
    // by the service; rows link to the business detail page.
    _renderPlgLeaderboard(businesses) {
        if (!businesses.length) {
            this.leaderboardTarget.innerHTML = `<p class="text-muted mb-0">No PLG activity to rank.</p>`;
            return;
        }
        const rows = businesses.map((b, i) => `
            <tr>
                <td class="text-muted">${i + 1}</td>
                <td><a href="/admin/businesses/${encodeURIComponent(b.id)}">${esc(b.name)}</a>${b.is_demo ? ' <span class="badge bg-warning text-dark">demo</span>' : ""}</td>
                <td class="text-end fw-semibold">${esc((b.quotes_accepted_online || 0).toLocaleString())}</td>
                <td class="text-end">${esc((b.emails_sent || 0).toLocaleString())}</td>
            </tr>`).join("");
        this.leaderboardTarget.innerHTML = `
            <div class="table-responsive">
                <table class="table table-sm table-hover align-middle mb-0">
                    <thead>
                        <tr>
                            <th>#</th><th>Business</th>
                            <th class="text-end">Accepted online</th>
                            <th class="text-end">Emails sent</th>
                        </tr>
                    </thead>
                    <tbody>${rows}</tbody>
                </table>
            </div>`;
    }

    // Ranked per-business table, only on the platform page (leaderboard target).
    // Each row links to that business's detail page (its own embedded panel).
    _renderLeaderboard(businesses) {
        if (!businesses.length) {
            this.leaderboardTarget.innerHTML = `<p class="text-muted mb-0">No real-tenant activity to rank.</p>`;
            return;
        }
        const rows = businesses.map((b, i) => `
            <tr>
                <td class="text-muted">${i + 1}</td>
                <td><a href="/admin/businesses/${encodeURIComponent(b.id)}">${esc(b.name)}</a>${b.is_demo ? ' <span class="badge bg-warning text-dark">demo</span>' : ""}</td>
                <td class="text-end fw-semibold">${esc((b.activity_total || 0).toLocaleString())}</td>
                <td class="text-end">${esc((b.current && b.current.quotes_created || 0).toLocaleString())}</td>
                <td class="text-end">${esc((b.current && b.current.jobs_created || 0).toLocaleString())}</td>
                <td class="text-end">${esc((b.current && b.current.invoices_sent || 0).toLocaleString())}</td>
            </tr>`).join("");
        this.leaderboardTarget.innerHTML = `
            <div class="table-responsive">
                <table class="table table-sm table-hover align-middle mb-0">
                    <thead>
                        <tr>
                            <th>#</th><th>Business</th>
                            <th class="text-end">Activity</th>
                            <th class="text-end">Quotes</th>
                            <th class="text-end">Jobs</th>
                            <th class="text-end">Invoices sent</th>
                        </tr>
                    </thead>
                    <tbody>${rows}</tbody>
                </table>
            </div>`;
    }

    _syncPeriodButtons() {
        if (!this.hasPeriodTarget) return;
        this.periodTarget.querySelectorAll("[data-days]").forEach((btn) => {
            const active = parseInt(btn.dataset.days, 10) === this.daysValue;
            btn.classList.toggle("active", active);
        });
    }

    _setStatus(text, isError) {
        if (!this.hasStatusTarget) return;
        this.statusTarget.textContent = text;
        this.statusTarget.classList.toggle("text-danger", !!isError);
    }
}
